import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon, ClockIcon, InfoIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { blogPosts, getBlogPostBySlug, getBlogImage } from '../data/allBlogPosts';
import type { ContentBlock } from '../data/seoBlogArticles';
import { buildArticleSchema, buildBreadcrumbList } from '../lib/structuredData';
const renderContentBlock = (block: ContentBlock, index: number) => {
  switch (block.type) {
    case 'heading':
      return <h2 key={index} className="text-2xl md:text-3xl font-bold text-[#111827] mt-12 mb-5" data-id="element-1032" data-mp="1cezpfajk3nfe">
          {block.content}
        </h2>;
    case 'heading3':
      return <h3 key={index} className="text-xl md:text-2xl font-bold text-[#111827] mt-10 mb-4" data-id="element-1033" data-mp="24xddwjj05x1t">
          {block.content}
        </h3>;
    case 'paragraph':
      return <p key={index} className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-1034" data-mp="gbdtctv84zai">
          {block.content}
        </p>;
    case 'list':
      return <ul key={index} className="space-y-3 mb-8" data-id="element-1035" data-mp="p9z7mctnxs5h">
          {block.items?.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1036" data-mp="13yvngw1yo9gn">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#004AC7] flex-shrink-0" aria-hidden="true" data-id="element-1037" data-mp="141htgr97egwr" />
              <span className="text-lg text-[#4B5563] leading-relaxed" data-id="element-1038" data-mp="1cz0jka1pflfi">
                {item}
              </span>
            </li>)}
        </ul>;
    case 'callout':
      return <div key={index} className="bg-[#F7F8FA] border-l-4 border-[#004AC7] p-6 rounded-r-lg my-10" data-id="element-1039" data-mp="3eshoh5yga86k">
          <div className="flex items-start gap-4" data-id="element-1040" data-mp="39u6dgrwf5rmr">
            <InfoIcon className="w-6 h-6 text-[#004AC7] flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-1041" data-mp="1dpqjffrlo9n7" />
            <p className="text-lg text-[#111827] font-medium leading-relaxed m-0" data-id="element-1042" data-mp="1iotp92femjw8">
              {block.content}
            </p>
          </div>
        </div>;
    default:
      return null;
  }
};
export const BlogPostPage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = getBlogPostBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!post) {
    return <Navigate to="/bibliotheek" replace data-id="element-1043" data-mp="21ktbdwlpat7w" />;
  }
  const related = post.relatedPosts.map(relatedSlug => getBlogPostBySlug(relatedSlug)).filter((item): item is NonNullable<typeof item> => Boolean(item)).slice(0, 3);
  const fallbackRelated = blogPosts.filter(item => item.slug !== post.slug && item.category === post.category).slice(0, 3);
  const relatedToShow = related.length > 0 ? related : fallbackRelated;
  const heroImage = getBlogImage(post);
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-1044" data-mp="8nxtiw9guct4">
      <SEO title={`${post.title} | Noord Digital`} description={post.metaDescription} keywords={post.keywords} canonical={`https://noorddigital.nl/blog/${post.slug}`} ogType="article" ogImage={heroImage} jsonLd={[buildArticleSchema({
      headline: post.title,
      description: post.metaDescription,
      path: `/blog/${post.slug}`,
      section: post.category,
      image: heroImage
    }), buildBreadcrumbList([{
      name: 'Bibliotheek',
      path: '/bibliotheek'
    }, {
      name: post.category,
      path: '/bibliotheek'
    }, {
      name: post.title,
      path: `/blog/${post.slug}`
    }])]} data-id="element-1045" data-mp="3pw8w6we65fj" />
      <Header data-id="element-1046" data-mp="pxi4yyldsug3" />

      <main data-id="element-1047" data-mp="px03nnjv3q1b">
        {/* Hero */}
        <div className="bg-[#F7F8FA] pt-28 pb-14 md:pt-32 border-b border-gray-200" data-id="element-1048" data-mp="uxmoyyli07b2">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1049" data-mp="uvjsbvpubfxd">
            <nav aria-label="Kruimelpad" className="mb-8" data-id="element-1050" data-mp="gzpr7yvb5uut">
              <ol className="flex items-center gap-1.5 text-sm text-[#6B7280] flex-wrap" data-id="element-1051" data-mp="lw6eyryli77d">
                <li data-id="element-1052" data-mp="19m0rf8f05tr3">
                  <Link to="/" className="hover:text-[#004AC7] transition-colors duration-200" data-id="element-1053" data-mp="19nlnj2gytkr2">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-1054" data-mp="zwh7dzfjztun">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" data-id="element-1055" data-mp="14vkd7m3cy43o" />
                </li>
                <li data-id="element-1056" data-mp="1slog1r8q1ztm">
                  <Link to="/bibliotheek" className="hover:text-[#004AC7] transition-colors duration-200" data-id="element-1057" data-mp="1sl4u7zfyarab">
                    Bibliotheek
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-1058" data-mp="26ou47ysdu8a7">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" data-id="element-1059" data-mp="22pv20ly1cpmg" />
                </li>
                <li className="font-medium text-[#111827]" aria-current="page" data-id="element-1060" data-mp="2ggglabrej1kv">
                  {post.category}
                </li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-id="element-1061" data-mp="1nt3z42p5kvs3">
              <motion.div className="lg:col-span-7" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-1062" data-mp="1jqzcvp11b1fh">
                <span className="inline-block bg-[#004AC7] text-white text-xs font-semibold px-3 py-1 rounded-full mb-5" data-id="element-1063" data-mp="3459c5tt62j8m">
                  {post.category}
                </span>
                <h1 className="font-bold text-[#111827] text-3xl md:text-4xl lg:text-5xl leading-tight mb-5" data-id="element-1064" data-mp="3g82j3o5lu253">
                  {post.title}
                </h1>
                <p className="text-[#4B5563] text-lg leading-relaxed mb-5" data-id="element-1065" data-mp="3l5lvsjmnm6ux">
                  {post.excerpt}
                </p>
                <p className="inline-flex items-center gap-2 text-sm text-[#6B7280]" data-id="element-1066" data-mp="2sjaz3k264q3e">
                  <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1067" data-mp="2xi40wf83esnq" />
                  {post.readingTime} minuten lezen
                </p>
              </motion.div>

              <div className="lg:col-span-5" data-id="element-1068" data-mp="32fw3c36d5x66">
                <img src={heroImage} alt={`${post.title}, artikel van Noord Digital`} className="w-full h-56 md:h-72 object-cover rounded-2xl shadow-lg" data-id="element-1069" data-mp="377nnnvyxf6nv" />
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <article className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[760px] py-14 md:py-16" data-id="element-1070" data-mp="278ddlnjey2xd">
          {post.content.map(renderContentBlock)}

          <div className="mt-14 bg-[#004AC7] rounded-2xl p-8 md:p-10" data-id="element-1071" data-mp="226xrrt9evsdx">
            <h2 className="text-white font-bold text-2xl md:text-3xl mb-3" data-id="element-1072" data-mp="1x8mfzczk37t2">
              Wil je dit voor je eigen bedrijf op orde hebben?
            </h2>
            <p className="text-white/85 leading-relaxed mb-6" data-id="element-1073" data-mp="1o8zeysaqo40j">
              In een gesprek van een half uur kijken we naar je huidige
              vindbaarheid en vertellen we wat we zouden aanpakken. Je zit
              daarna tot niets verplicht.
            </p>
            <a href="/#contact" className="inline-flex items-center gap-2 bg-white text-[#004AC7] font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors duration-200" data-id="element-1074" data-mp="1ehbtd80v5h6z">
              Plan een kennismaking
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1075" data-mp="10kpxg2kk62f7" />
            </a>
          </div>
        </article>

        {/* Related */}
        {relatedToShow.length > 0 && <section className="w-full bg-[#F7F8FA] py-16 md:py-20 border-t border-gray-200" data-id="element-1076" data-mp="2tfz8wd0kwnwv">
            <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1077" data-mp="2eioq0illwfr4">
              <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-10" data-id="element-1078" data-mp="2jh9vawfptpmf">
                Verder lezen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1079" data-mp="25otwy8xrefru">
                {relatedToShow.map(item => <Link key={item.slug} to={`/blog/${item.slug}`} className="group bg-white rounded-2xl border border-gray-200 p-6 flex flex-col hover:border-[#004AC7] transition-colors duration-200" data-id="element-1080" data-mp="2ai4u1icrri6k">
                    <span className="text-xs font-semibold text-[#004AC7] mb-3" data-id="element-1081" data-mp="1ms24pa08oqbn">
                      {item.category}
                    </span>
                    <h3 className="font-bold text-[#111827] text-lg mb-3" data-id="element-1082" data-mp="1qufa1xb2qm7w">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#6B7280] leading-relaxed mb-5 flex-grow" data-id="element-1083" data-mp="135qryyq5swwg">
                      {item.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-[#004AC7] font-semibold text-sm mt-auto" data-id="element-1084" data-mp="1h69wvwzbvmue">
                      Lees verder
                      <ArrowRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-200" aria-hidden="true" data-id="element-1085" data-mp="1r2d0z8dqpvjz" />
                    </span>
                  </Link>)}
              </div>

              <div className="mt-12" data-id="element-1086" data-mp="1vtmr0a37b0we">
                <Link to="/bibliotheek" className="inline-flex items-center gap-2 text-[#004AC7] font-semibold hover:text-[#003A9E] transition-colors duration-200" data-id="element-1087" data-mp="1vuo92yimwnnm">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1088" data-mp="24sqavga6zkby" />
                  Terug naar de bibliotheek
                </Link>
              </div>
            </div>
          </section>}
      </main>

      <Footer data-id="element-1089" data-mp="wfub641jfyhn" />
    </div>;
};